<template>
	<div class="dialog draggable-item bg-light light w-100-sm w-100-md w-75-lg w-50-xl h-75">
		<div class="dialog-title pl-10 d-flex flex-align-center">
			<span v-if="mode == 'tickets'" class="flex-1">{{ $t("Generate invoice from tickets") }}</span>
			<span v-else class="flex-1">{{ $t("Generate invoice from date range") }}</span>
			<span class="button square closer" @click="$root.$emit('CLOSEINVOICEDIALOG')"></span>
		</div>

		<!-- STEP 0: dati obbligatori -->
		<div v-if="step == STEP_BASE_INFOS" class="dialog-content overflow">
			<div class="invoice-group">
				<label class="text-bold mb-1 d-block">{{ $t("Customer") }} <span class="fg-red">*</span></label>
				<select class="select w-100 mb-2" v-model="invoice.id_customer" @change="onCustomerChange">
					<option :value="''">{{ $t("Select a customer") }}</option>
					<option v-for="c in customers" :value="c.id" :key="c.id">{{ c.company_name }}</option>
				</select>
			</div>
			<template v-if="invoice.id_customer">
				<!-- Mod A: ticket selection -->
				<div v-if="mode == 'tickets'" class="invoice-group mt-3">
					<h6 class="border-bottom pb-1 text-bold">{{ $t("Unbilled tickets") }} <span class="fg-red">*</span></h6>
					<div v-if="loadingTickets" class="text-small fg-dark">
						<span class="mif-spinner2 ani-spin mr-1"></span>{{ $t("Loading tickets...") }}
					</div>
					<div v-else-if="tickets.length == 0" class="text-small fg-dark">
						{{ $t("No completed tickets available for this customer") }}
					</div>
					<table v-else class="table compact striped w-100">
						<thead>
							<tr>
								<th class="text-center" style="width:40px;">
									<input type="checkbox" data-role="checkbox" :checked="allTicketsSelected" @change="toggleAllTickets" />
								</th>
								<th>{{ $t("ID") }}</th>
								<th>{{ $t("Title") }}</th>
								<th>{{ $t("Closed") }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="t in tickets" :key="t.id">
								<td class="text-center">
									<input type="checkbox" data-role="checkbox" :value="t.id" v-model="invoice.ticket_ids" />
								</td>
								<td class="text-small">#{{ t.id }}</td>
								<td>{{ t.title || "-" }}</td>
								<td class="text-small">{{ formatDate(t.updated_at) }}</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Mod B: date range -->
				<div v-if="mode == 'range'" class="invoice-group mt-3">
					<h6 class="border-bottom pb-1 text-bold">{{ $t("Period") }} <span class="fg-red">*</span></h6>
					<div class="d-flex flex-justify-between">
						<div class="w-50 pr-2">
							<label class="text-bold mb-1 d-block">{{ $t("From") }}</label>
							<input type="date" class="input w-100 mb-2" v-model="invoice.date_from" />
						</div>
						<div class="w-50 pl-2">
							<label class="text-bold mb-1 d-block">{{ $t("To") }}</label>
							<input type="date" class="input w-100 mb-2" v-model="invoice.date_to" />
						</div>
					</div>
				</div>
			</template>
		</div>

		<!-- STEP 1: dati facoltativi -->
		<div v-if="step == STEP_ADD_INFOS" class="dialog-content overflow">
			<div class="invoice-group">
				<h6 class="border-bottom pb-1 text-bold">{{ $t("Additional info") }}</h6>
				<div class="text-small fg-dark mb-3">{{ $t("All fields in this step are optional.") }}</div>
				<div>
					<label class="text-bold mb-1 d-block">{{ $t("Invoice reference") }}</label>
					<input class="input w-100 mb-2" v-model="invoice.external_invoice_id" maxlength="100" :placeholder="$t('e.g. FT/2026/0042')" />
				</div>
				<div>
					<label class="text-bold mb-1 d-block">{{ $t("Notes") }}</label>
					<textarea class="input w-100 mb-2" rows="4" v-model="invoice.note" :placeholder="$t('Optional notes')"></textarea>
				</div>
			</div>
		</div>

		<!-- STEP 2: risultato generate -->
		<div v-if="step == STEP_INVOICE_VIEW" class="dialog-content overflow p-3">
			<div v-if="!completeInvoice" class="text-small fg-dark p-3">
				<span class="mif-warning mr-1"></span>{{ $t("No invoice data available.") }}
			</div>
			<InvoiceTemplate v-else :invoice="completeInvoice" />
		</div>

		<!-- AZIONI -->
		<div class="dialog-actions p-3 border-top text-right">
			<!-- Step 0 -->
			<template v-if="step == STEP_BASE_INFOS">
				<button class="button" @click="$root.$emit('CLOSEINVOICEDIALOG')">{{ $t("Cancel") }}</button>
				<button class="button secondary ml-2" :disabled="!canProceedFromStep0" v-disable-tooltip:step0_disabled_message @click="next">
					{{ $t("Next") }}
				</button>
			</template>

			<!-- Step 1 -->
			<template v-if="step == STEP_ADD_INFOS">
				<button class="button" @click="back">
					{{ $t("Back") }}
				</button>
				<button class="button secondary ml-2" :disabled="generating" @click="generate">
					<span v-if="generating" class="mif-spinner2 ani-spin mr-1"></span>
					{{ $t("Generate") }}
				</button>
			</template>

			<!-- Step 2 -->
			<template v-if="step == STEP_INVOICE_VIEW">
				<button class="button" :disabled="exporting" @click="exportInvoiceAs(completeInvoice, 'pdf')">
					<span v-if="exporting == 'pdf'" class="mif-spinner2 ani-spin mr-1"></span>
					<span v-else class="mif-file-pdf mr-1"></span>
					{{ $t("Export PDF") }}
				</button>
				<button class="button ml-2" :disabled="exporting" @click="exportInvoiceAs(completeInvoice, 'xlsx')">
					<span v-if="exporting == 'xlsx'" class="mif-spinner2 ani-spin mr-1"></span>
					<span v-else class="mif-file-excel mr-1"></span>
					{{ $t("Export XLSX") }}
				</button>
				<button class="button secondary ml-2" @click="$root.$emit('CLOSEINVOICEDIALOG')">{{ $t("Close") }}</button>
			</template>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import { TICKET_STATUS, populateUsefulMaps } from '../../../utils/entityMethods/ticket.js';
import InvoiceTemplate from './InvoiceTemplate.vue';
import { invoiceExportMixin } from '../../../mixins/invoiceExport';

export default {
	name: "InvoiceDialog",
	components: { InvoiceTemplate },
	mixins: [invoiceExportMixin],
	props: {
		invoice: {
			type: Object,
			required: true
		},
		mode: String
	},
	data() {
		return {
			step: 0,
			STEP_BASE_INFOS: 0,
			STEP_ADD_INFOS: 1,
			STEP_INVOICE_VIEW: 2,

			customers: [],
			tickets: [],
			loadingTickets: false,
			generating: false,

			completeInvoice: null,
			step0_disabled_message: ""
		};
	},
	computed: {
		allTicketsSelected() {
			return this.tickets.length > 0
				&& this.invoice.ticket_ids.length == this.tickets.length;
		},
		canProceedFromStep0() {
			if (!this.invoice.id_customer) {
				this.step0_disabled_message = this.$t("Select a customer");
				return false;
			}
			if (this.mode == "tickets") {
				if (!this.invoice.ticket_ids || this.invoice.ticket_ids.length == 0) {
					this.step0_disabled_message = this.$t("Select at least one ticket");
					return false;
				}
			} else {
				if (!this.invoice.date_from || !this.invoice.date_to) {
					this.step0_disabled_message = this.$t("Set both dates");
					return false;
				}
				if (moment(this.invoice.date_to).isBefore(this.invoice.date_from)) {
					this.step0_disabled_message = this.$t("End date must be after start date");
					return false;
				}
			}
			this.step0_disabled_message = "";
			return true;
		}
	},
	async mounted() {
		await this.populateUsefulMaps();
		this.customers = Object.values(this.$root.customerMap);
	},
	methods: {
		populateUsefulMaps,
		next() {
			if (this.step == this.STEP_BASE_INFOS && !this.canProceedFromStep0) return;
			if (this.step < this.STEP_INVOICE_VIEW) this.step++;
		},
		back() {
			if (this.step > this.STEP_BASE_INFOS) this.step--;
		},
		async onCustomerChange() {
			this.invoice.ticket_ids = [];
			this.tickets = [];
			if (!this.invoice.id_customer) return;
			await this.loadAvailableTickets();
		},
		async loadAvailableTickets() {
			this.loadingTickets = true;
			this.tickets = await this.$api.getTickets({
				id_customer: this.invoice.id_customer,
				status: TICKET_STATUS.COMPLETED,
				//limit: 500,
				invoiced: false
			});
			this.loadingTickets = false;
		},
		toggleAllTickets(e) {
			this.invoice.ticket_ids = e.target.checked
				? this.tickets.map(t => t.id)
				: [];
		},
		formatDate(d) {
			if (!d) return "-";
			return moment(d).format("DD/MM/YYYY HH:mm");
		},
		durationLabel(minutes) {
			if (minutes == null) return "-";
			const h = Math.floor(minutes / 60);
			const m = minutes % 60;
			return (h > 0 ? h + "h " : "") + m + "m";
		},
		buildPayload() {
			const base = {
				id_customer: this.invoice.id_customer
			};
			if (this.invoice.external_invoice_id) base.external_invoice_id = this.invoice.external_invoice_id;
			if (this.invoice.note) base.note = this.invoice.note;

			if (this.mode == "tickets") {
				return { ...base, ticket_ids: this.invoice.ticket_ids };
			}
			return {
				...base,
				date_from: moment(this.invoice.date_from).format("YYYYMMDD") + "000000",
				date_to: moment(this.invoice.date_to).format("YYYYMMDD") + "235959"
			};
		},
		async generate() {
			if (this.generating) return;
			this.generating = true;
			const completeInvoice = await this.$api.generateInvoice(this.buildPayload());
			this.generating = false;
			if (!completeInvoice) return;
			this.completeInvoice = completeInvoice;
			this.$root.$emit("refreshInvoices");
			this.step = this.STEP_INVOICE_VIEW;
		}
		// export PDF/XLSX forniti dal mixin invoiceExportMixin
	}
};
</script>
<style scoped>
.flex-1 {
	flex: 1;
}

.tree-container {
	max-height: 50vh;
	overflow: auto;
}
</style>
